import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Users, Radio, ArrowLeft, Play, Loader2, Sparkles } from "lucide-react";

/**
 * WatchPartyList — dedicated /parties page showing every currently-live watch
 * party. Users can jump straight in without an invite link.
 */
const CATEGORY_ICONS = { general: "🎬", music: "🎵", drama: "🎭", comedy: "😂", dance: "💃", sports: "⚽", gaming: "🎮", vlog: "📹" };

export default function WatchPartyList() {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState(["general","music","drama","comedy","dance","sports","gaming","vlog"]);

  const load = () => {
    const q = category && category !== "all" ? `?category=${category}` : "";
    api.get(`/watch-parties/active${q}`)
      .then(r => setParties(r.data || []))
      .catch(() => setParties([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    api.get("/watch-parties/categories")
      .then(r => setCategories(r.data?.categories || categories))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="px-4 py-6 md:max-w-4xl md:mx-auto" data-testid="parties-page">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></Link>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter flex items-center gap-2"><Sparkles className="w-6 h-6 text-[#FF2A5F]"/>Watch Parties</h1>
      </div>
      <p className="text-white/60 text-sm mb-3">Live-synced reel viewing rooms hosted by other Hifireels users. Tap Join to watch together.</p>

      {/* Category filter chips */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1 mb-3" data-testid="parties-filter">
        {["all", ...categories].map(c => (
          <button key={c} onClick={() => setCategory(c)}
            data-testid={`parties-cat-${c}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-extrabold flex items-center gap-1 border ${category === c ? "bg-[#FF2A5F] border-[#FF2A5F] text-white" : "bg-white/5 border-white/10 text-white/70"}`}>
            <span>{c === "all" ? "✨" : (CATEGORY_ICONS[c] || "🎬")}</span>
            {c === "all" ? "All" : c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-[#FF2A5F]"/></div>
      ) : parties.length === 0 ? (
        <div className="text-center py-12 text-white/60" data-testid="parties-empty">
          <div className="text-5xl mb-3">🎬</div>
          <div className="font-bold text-lg">No active watch parties right now</div>
          <div className="text-sm text-white/50 mt-1">Start your own from any reel's More menu → "Start Watch Party".</div>
          <Link to="/" className="inline-block mt-4 px-4 py-2 rounded-full bg-[#FF2A5F] text-white text-sm font-extrabold" data-testid="parties-browse">Browse reels →</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="parties-grid">
          {parties.map(p => (
            <Link key={p.id} to={`/watch/${p.id}`} data-testid={`party-card-${p.id}`}
              className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-[#FF2A5F]/30 hover:border-[#FF2A5F]/60 active:scale-[0.98] transition">
              <div className="relative w-20 h-28 rounded-xl overflow-hidden bg-black flex-shrink-0">
                {p.reel?.thumbnail_url ? (
                  <img src={p.reel.thumbnail_url} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl">📹</div>
                )}
                <div className="absolute top-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FF2A5F] text-white text-[8px] font-extrabold">
                  <Radio className="w-2 h-2 animate-pulse"/>LIVE
                </div>
                <Play className="absolute inset-0 m-auto w-8 h-8 text-white/85"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#A855F7]/20 text-[#A855F7] font-extrabold" data-testid={`party-cat-${p.id}`}>
                    {CATEGORY_ICONS[p.category] || "🎬"} {p.category || "general"}
                  </span>
                </div>
                <div className="font-bold text-sm truncate">{p.title}</div>
                <div className="text-[11px] text-white/60 truncate">by @{p.host?.username || "host"}</div>
                <div className="text-[11px] text-white/70 truncate mt-1">🎬 {p.reel?.title || "Untitled reel"}</div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 flex items-center gap-1"><Users className="w-2.5 h-2.5"/>{(p.viewer_ids || []).length}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00E5FF] text-black font-extrabold">Join →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
